import {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { Box, OutlinedInput } from "@mui/material";
import {
  OTP_CODE_LENGTH,
  NON_DIGIT_REGEX,
} from "Components/Shared/PhoneVerification/constants";

const SINGLE_DIGIT_REGEX = /^\d$/;
const OPTIONAL_SINGLE_DIGIT_REGEX = /^\d?$/;

const createEmptyDigits = () =>
  Array.from({ length: OTP_CODE_LENGTH }, () => "");

export type OtpCodeInputHandle = {
  reset: () => void;
};

type Props = {
  onChange: (value: string) => void;
};

export const OtpCodeInput = forwardRef<OtpCodeInputHandle, Props>(
  ({ onChange }, ref) => {
    const [digits, setDigits] = useState(createEmptyDigits);
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const focusInput = useCallback((index: number) => {
      inputRefs.current[index]?.focus();
    }, []);

    const updateDigits = useCallback(
      (newDigits: Array<string>) => {
        setDigits(newDigits);
        onChange(newDigits.join(""));
      },
      [onChange],
    );

    const handleChange = useCallback(
      (index: number, digit: string) => {
        if (!OPTIONAL_SINGLE_DIGIT_REGEX.test(digit)) {
          return;
        }

        const newDigits = [...digits];
        newDigits[index] = digit;
        updateDigits(newDigits);

        if (digit && index < OTP_CODE_LENGTH - 1 && !digits[index + 1]) {
          focusInput(index + 1);
        }
      },
      [digits, updateDigits, focusInput],
    );

    const handleKeyDown = useCallback(
      (
        index: number,
        e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        if (e.key === "Backspace") {
          e.preventDefault();
          if (digits[index]) {
            const newDigits = [...digits];
            newDigits[index] = "";
            updateDigits(newDigits);
          } else if (index > 0) {
            focusInput(index - 1);
          }
          return;
        }

        if (SINGLE_DIGIT_REGEX.test(e.key)) {
          e.preventDefault();
          handleChange(index, e.key);
          return;
        }

        if (e.key === "ArrowLeft" && index > 0) {
          e.preventDefault();
          focusInput(index - 1);
        }

        if (e.key === "ArrowRight" && index < OTP_CODE_LENGTH - 1) {
          e.preventDefault();
          focusInput(index + 1);
        }
      },
      [digits, updateDigits, focusInput, handleChange],
    );

    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData
          .getData("text")
          .replace(NON_DIGIT_REGEX, "")
          .slice(0, OTP_CODE_LENGTH);

        if (pasted.length > 0) {
          const newDigits = createEmptyDigits();
          for (let i = 0; i < pasted.length; i++) {
            newDigits[i] = pasted[i];
          }
          updateDigits(newDigits);
          focusInput(Math.min(pasted.length, OTP_CODE_LENGTH - 1));
        }
      },
      [updateDigits, focusInput],
    );

    useImperativeHandle(ref, () => ({
      reset: () => {
        updateDigits(createEmptyDigits());
        focusInput(0);
      },
    }));

    return (
      <Box display="flex" gap={1} justifyContent="center">
        {digits.map((digit, index) => (
          <OutlinedInput
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP boxes
            key={index}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            autoFocus={index === 0}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value.slice(-1))}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              pattern: "[0-9]",
              sx: {
                textAlign: "center",
                fontSize: "1.5rem",
                fontWeight: 600,
                padding: 0,
              },
            }}
            sx={{
              width: 48,
              height: 56,
              padding: "8px",
            }}
          />
        ))}
      </Box>
    );
  },
);
